import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useAuth } from "@bw/shared/hooks";
import type { LeagueStandingResponse } from "@bw/shared/types/generated";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { leagueApi } from "@bw/shared/api";
import { queryKeys } from "../../../common/query/queryClient";
import { playerHandleSuffix } from "@bw/shared/components";
import { Page } from "../../../common/components/Page";
import { DetailHeader, ShareButton } from "../../../common/components/DetailHeader";
import { SegmentedNav } from "../../../common/components/SegmentedNav";
import { visuallyHidden } from "../../../common/visuallyHidden";
import { useDivisionStandings } from "../hooks/useDivisionStandings";

export function LeagueStandingsPage() {
  const { divisionId = "" } = useParams<{ divisionId: string }>();
  const parent = useQuery({
    queryKey: [...queryKeys.leagues.all, "public-division", divisionId],
    queryFn: () => leagueApi.getPublicDivision(divisionId),
    enabled: Boolean(divisionId),
  });
  const { user } = useAuth();
  const { data, isLoading, isError, refetch } =
    useDivisionStandings(divisionId);
  const standings = data ?? [];
  const title = parent.data?.division_name || "Division standings";
  // "Winter Ladder · Div A": the league names the division in the header.
  const heading = parent.data?.division_name
    ? [parent.data.league_name, parent.data.division_name]
        .filter(Boolean)
        .join(" · ")
    : title;
  const context = [
    parent.data?.season_name,
    parent.data?.facility_name,
  ]
    .filter(Boolean)
    .join(" · ");
  const players = standings.length;

  return (
    <Page narrow>
      <Stack spacing={1.5}>
        <DetailHeader
          title={
            parent.data?.division_name ? (
              <DivisionTitle
                league={parent.data.league_name}
                division={parent.data.division_name}
              />
            ) : (
              heading
            )
          }
          subtitle={
            [players > 0 ? `${players} ${players === 1 ? "player" : "players"}` : null, context]
              .filter(Boolean)
              .join(" · ") || undefined
          }
          back={{ to: "/compete/leagues", label: "Back to leagues" }}
          actions={<ShareButton title={heading} />}
        />
        <DivisionNav divisionId={divisionId} active="standings" />
        {parent.isError ? (
          <Alert
            severity="warning"
            action={
              <Button onClick={() => void parent.refetch()}>
                Retry context
              </Button>
            }
          >
            Division context is unavailable.
          </Alert>
        ) : null}
        {isLoading ? (
          <Typography color="text.secondary" role="status">
            Loading standings…
          </Typography>
        ) : isError ? (
          <Alert
            severity="error"
            role="alert"
            action={<Button onClick={() => void refetch()}>Retry</Button>}
          >
            We couldn&apos;t load the standings. Please try again.
          </Alert>
        ) : data !== undefined && standings.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography color="text.secondary">No standings yet.</Typography>
          </Paper>
        ) : (
          <TableContainer>
            <Typography component="h2" sx={{ fontSize: "1rem", fontWeight: 700, mb: 0.5 }}>
              Standings
            </Typography>
            <Table
              size="small"
              aria-label={`${title} standings`}
              sx={{
                "& td, & th": { borderColor: "divider", px: 0.5, py: 0.75 },
                "& th": { fontSize: "0.75rem", color: "text.secondary" },
              }}
            >
              <TableHead>
                <TableRow role="row">
                  <TableCell sx={{ width: 28 }}>
                    <span aria-hidden>#</span>
                    <Box component="span" sx={visuallyHidden}>
                      Rank
                    </Box>
                  </TableCell>
                  <TableCell>Player</TableCell>
                  <TableCell align="right">W–L</TableCell>
                  <TableCell align="right">Played</TableCell>
                  <TableCell align="right">
                    <span aria-hidden>Pts</span>
                    <Box component="span" sx={visuallyHidden}>
                      Points
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {standings.map((row, index) => (
                  <StandingRow
                    key={row.standing_id}
                    row={row}
                    rank={index + 1}
                    isOwn={Boolean(user) && row.player_id === user?.uid}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>
    </Page>
  );
}

function StandingRow({
  row,
  rank,
  isOwn,
}: {
  row: LeagueStandingResponse;
  rank: number;
  isOwn: boolean;
}) {
  const name = row.display_name?.trim();
  const cell = {
    fontSize: "0.8125rem",
    fontVariantNumeric: "tabular-nums",
    color: isOwn ? "primary.main" : "text.primary",
    fontWeight: isOwn ? 700 : 400,
  } as const;
  return (
    <TableRow role="row" aria-current={isOwn ? "true" : undefined}>
      <TableCell sx={cell}>{rank}</TableCell>
      <TableCell sx={{ ...cell, maxWidth: 0, width: "55%" }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", minWidth: 0 }}>
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            <span>{name || "—"}</span>
            {name && row.player_id ? (
              <Box component="span" sx={{ color: isOwn ? "inherit" : "text.secondary" }}>
                {` ${playerHandleSuffix(row.player_id)}`}
              </Box>
            ) : null}
          </Box>
          {isOwn ? <Chip label="You" size="small" color="primary" /> : null}
        </Stack>
      </TableCell>
      <TableCell align="right" sx={cell}>
        {row.wins}-{row.losses}
      </TableCell>
      <TableCell align="right" sx={cell}>{row.matches_played}</TableCell>
      <TableCell align="right" sx={cell}>{row.points}</TableCell>
    </TableRow>
  );
}

/** Standings and the season's sessions, one segmented switch per division. */
export function DivisionNav({
  divisionId,
  active,
}: {
  divisionId: string;
  active: "standings" | "sessions";
}) {
  return (
    <SegmentedNav
      label="Division views"
      active={active}
      items={[
        {
          id: "standings",
          label: "Standings",
          to: `/compete/leagues/divisions/${divisionId}/standings`,
        },
        {
          id: "sessions",
          label: "Sessions",
          to: `/compete/leagues/divisions/${divisionId}`,
        },
      ]}
    />
  );
}

/** "Winter Ladder · Div A": each part is its own text run. */
export function DivisionTitle({
  league,
  division,
}: {
  league?: string | null;
  division: string;
}) {
  return (
    <>
      {league ? (
        <>
          <span>{league}</span>
          {" · "}
        </>
      ) : null}
      <span>{division}</span>
    </>
  );
}
